import { Controller } from "@hotwired/stimulus"

// Dropdown del panel Admin: trigger kebab + panel flotante con items.
//
// Al abrir, mueve el panel al <body> para escapar de stacking contexts
// (overflow:hidden de <table>/_paper, transform de turbo-frames, etc.) y
// lo posiciona con position:fixed calculando espacio arriba/abajo.
// Cierra con click-outside, Esc, scroll del viewport o evento custom
// "admin:modal:open". Al abrir uno, cierra los demas dropdowns.
//
// Uso:
//   <%= admin_dropdown do %>
//     <%= admin_dropdown_item "Ver", href: ..., icon: "fa-regular fa-eye" %>
//     <%= admin_dropdown_item "Editar", href: ..., icon: "fa-regular fa-pen" %>
//   <% end %>

const VIEWPORT_MARGIN = 8
const GAP = 4
const openDropdowns = new Set()

export default class extends Controller {
  static targets = ["menu"]
  static values = { align: { type: String, default: "right" } }

  connect() {
    this._isOpen = false
    this._menuNode = this.menuTarget
    this._originalParent = null
    this._originalNextSibling = null

    this._clickOutside = this.clickOutside.bind(this)
    this._escHandler = (e) => { if (e.key === "Escape") this.close() }
    this._scrollHandler = () => this.close()
    this._modalOpenHandler = () => this.close()

    // Listener delegado en el panel: al hacer click en un <a> o <button>
    // descendiente, cerrar el dropdown. No preventDefault: dejar que la
    // navegacion/submit siga su flujo natural.
    this._menuClickHandler = (e) => {
      const item = e.target.closest("a, button")
      if (item && this._menuNode.contains(item)) this.close()
    }

    this._menuNode.addEventListener("click", this._menuClickHandler)
    document.addEventListener("click", this._clickOutside)
    document.addEventListener("keydown", this._escHandler)
    document.addEventListener("admin:modal:open", this._modalOpenHandler)
    window.addEventListener("scroll", this._scrollHandler, { passive: true })
  }

  disconnect() {
    if (this._menuNode) {
      this._menuNode.removeEventListener("click", this._menuClickHandler)
    }
    document.removeEventListener("click", this._clickOutside)
    document.removeEventListener("keydown", this._escHandler)
    document.removeEventListener("admin:modal:open", this._modalOpenHandler)
    window.removeEventListener("scroll", this._scrollHandler)
    this.close()
  }

  toggle(event) {
    event.stopPropagation()
    event.preventDefault()

    if (this._isOpen) {
      this.close()
    } else {
      this.open(event.currentTarget)
    }
  }

  open(triggerEl) {
    openDropdowns.forEach((dd) => { if (dd !== this) dd.close() })

    const menu = this._menuNode
    const trigger = triggerEl || this.element.querySelector("button")

    if (!this._originalParent) {
      this._originalParent = menu.parentNode
      this._originalNextSibling = menu.nextSibling
    }

    document.body.appendChild(menu)

    menu.classList.remove("hidden")
    Object.assign(menu.style, {
      position: "fixed",
      visibility: "hidden",
      zIndex: "9999",
      top: "0",
      left: "0",
      maxHeight: "none",
      overflowY: "hidden",
    })

    const triggerRect = trigger.getBoundingClientRect()
    const menuWidth = menu.offsetWidth
    const menuHeight = menu.offsetHeight
    const vh = window.innerHeight
    const vw = window.innerWidth

    const spaceBelow = vh - triggerRect.bottom - VIEWPORT_MARGIN - GAP
    const spaceAbove = triggerRect.top - VIEWPORT_MARGIN - GAP

    let top
    let maxHeight

    if (menuHeight <= spaceBelow) {
      top = triggerRect.bottom + GAP
      maxHeight = spaceBelow
    } else if (menuHeight <= spaceAbove) {
      top = triggerRect.top - GAP - menuHeight
      maxHeight = spaceAbove
    } else if (spaceBelow >= spaceAbove) {
      top = triggerRect.bottom + GAP
      maxHeight = spaceBelow
    } else {
      top = VIEWPORT_MARGIN
      maxHeight = spaceAbove
    }

    let left
    if (this.alignValue === "left") {
      left = triggerRect.left
    } else {
      left = triggerRect.right - menuWidth
    }
    left = Math.max(VIEWPORT_MARGIN, Math.min(left, vw - VIEWPORT_MARGIN - menuWidth))

    Object.assign(menu.style, {
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      maxHeight: `${Math.round(maxHeight)}px`,
      overflowY: "auto",
      visibility: "visible",
    })

    this._isOpen = true
    openDropdowns.add(this)
  }

  close() {
    if (!this._isOpen) return

    const menu = this._menuNode
    menu.classList.add("hidden")
    menu.removeAttribute("style")
    this._isOpen = false
    openDropdowns.delete(this)

    requestAnimationFrame(() => {
      if (this._originalParent) {
        if (this._originalNextSibling && this._originalNextSibling.parentNode === this._originalParent) {
          this._originalParent.insertBefore(menu, this._originalNextSibling)
        } else {
          this._originalParent.appendChild(menu)
        }
      }
    })
  }

  clickOutside(event) {
    if (!this._isOpen) return
    if (!this.element.contains(event.target) && !this._menuNode.contains(event.target)) {
      this.close()
    }
  }
}
